import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { InceptionService, TranslationService } from '@proximus/ng-common';
import { Alert, AlertsService } from '@proximus/ng-common-components';
import { UpdatePassword } from '../../../../../store/actions/internet-settings.actions';
import {
  ApplicationState,
  getInternetPasswordTechnicalError,
} from '../../../../../store/reducers';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { OnDestroyNewHookDirective } from '../../../../../shared/hooks';

@Component({
  selector: 'mprd-update-internet-password',
  templateUrl: './update-internet-password.component.html',
})
export class UpdateInternetPasswordComponent
  extends OnDestroyNewHookDirective
  implements OnInit, OnDestroy
{
  @Input() loginId: string;

  @ViewChild('newPasswordInput') newPasswordInput;
  @ViewChild('confirmPasswordInput') confirmPasswordInput;

  updatePasswordSuccess = false;
  updatePasswordFailed = false;

  confirmPassword = '';
  password = '';
  isPasswordValid = false;
  isConfirmPasswordValid = false;
  hidePassword = true;
  hideConfirmPassword = true;

  constructor(
    private inceptionService: InceptionService,
    private elementRef: ElementRef,
    private alertService: AlertsService,
    private store: Store<ApplicationState>,
    private translationService: TranslationService,
  ) {
    super();
  }

  ngOnInit() {
    this.initInception();
  }

  updatePassword() {
    this.store.dispatch(
      new UpdatePassword({
        login: this.loginId,
        internetPassword: this.password,
      }),
    );
    this.store
      .select(getInternetPasswordTechnicalError)
      .pipe(takeUntil(this.unsub$))
      .subscribe((response) => {
        this.updatePasswordSuccess = response.success;
        this.updatePasswordFailed = response.error;
        this.dispatchAlert(
          this.updatePasswordSuccess,
          this.updatePasswordFailed,
        );
      });
    this.reset();
  }

  validatePassword() {
    this.isPasswordValid = this.password?.length === 8;
    this.validateConfirmPassword();
  }

  isNewPasswordEmpty() {
    return this.password?.length === 0;
  }

  validateConfirmPassword() {
    this.isConfirmPasswordValid = this.password === this.confirmPassword;
  }

  isConfirmPasswordEmpty() {
    return this.confirmPassword?.length === 0;
  }

  validate() {
    return this.isPasswordValid && this.isConfirmPasswordValid;
  }

  togglePasswordVisibility() {
    this.hidePassword = !this.hidePassword;
  }

  toggleConfirmPasswordVisibility() {
    this.hideConfirmPassword = !this.hideConfirmPassword;
  }

  private initInception() {
    this.inceptionService.init();
    this.inceptionService.panelAccordion(this.elementRef);
    this.inceptionService.forms(this.elementRef);
  }

  private reset() {
    this.newPasswordInput.reset();
    this.confirmPasswordInput.reset();
    this.password = '';
    this.confirmPassword = '';
    this.isPasswordValid = false;
    this.isConfirmPasswordValid = false;
    this.updatePasswordSuccess = false;
    this.updatePasswordFailed = false;
  }

  private dispatchAlert(
    updatePasswordSuccess: boolean,
    updatePasswordFailed: boolean,
  ): void {
    let title: string;
    let description: string;
    let type: 'error' | 'info' | 'success' | 'warning';
    let taggingData: any;

    if (updatePasswordSuccess) {
      title = this.translationService.getTranslatedValueFor(
        'internet.settings.password.update.confirmation.label',
      );
      description = this.translationService.getTranslatedValueFor(
        'internet.settings.password.update.success',
      );
      type = 'success';
      taggingData = {
        event: 'popup-impression',
        button: {
          zone: this.translationService.getTranslatedValueFor(
            'internet.settings.password.title',
          ),
          name: 'change success',
        },
      };
    } else if (updatePasswordFailed) {
      title = this.translationService.getTranslatedValueFor(
        'internet.settings.password.update.error.label',
      );
      description = this.translationService.getTranslatedValueFor(
        'internet.settings.password.update.error',
      );
      type = 'error';
      taggingData = {
        event: 'popup-impression',
        button: {
          zone: this.translationService.getTranslatedValueFor(
            'internet.settings.password.title',
          ),
          name: 'change error',
        },
        error: {
          type: 'backend',
          message: description,
        },
      };
    }
    const alert: Alert = {
      isClosable: true,
      alertKey: 'updateInternetPasswordAlert',
      title,
      description,
      type,
      taggingData,
    };
    this.alertService.dispatchAlert(alert);
  }
}
